import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/lib/i18n/LanguageContext";
import { LanguageRouter } from "@/components/LanguageRouter";
import { getLocalizedRoutes } from "@/components/LocalizedRoutes";
import { AuthProvider } from "@/hooks/useAuth";
import { CookieConsent } from "@/components/CookieConsent";
import { ExitIntentPopup } from "@/components/ExitIntentPopup";
import { ScrollToTop } from "@/components/ScrollToTop";
import { useCartSync } from "@/hooks/useCartSync";
import AdminRoute from "./components/admin/AdminRoute";

import NotFound from "./pages/NotFound";
import AuthVerify from "./pages/AuthVerify";

// Admin Pages — lazy loaded (only needed for admin users)
const AdminLogin = React.lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard"));
const CardTemplates = React.lazy(() => import("./pages/admin/CardTemplates"));
const CardTemplateEditor = React.lazy(() => import("./pages/admin/CardTemplateEditor"));
const AdminOrders = React.lazy(() => import("./pages/admin/AdminOrders"));
const OrderDetail = React.lazy(() => import("./pages/admin/OrderDetail"));
const AdminCustomers = React.lazy(() => import("./pages/admin/AdminCustomers"));
const CustomerDetail = React.lazy(() => import("./pages/admin/CustomerDetail"));
const AdminInvoices = React.lazy(() => import("./pages/admin/AdminInvoices"));
const InvoiceDetail = React.lazy(() => import("./pages/admin/InvoiceDetail"));
const AdminProducts = React.lazy(() => import("./pages/admin/AdminProducts"));
const ProductDetail = React.lazy(() => import("./pages/admin/ProductDetail"));
const AdminSettings = React.lazy(() => import("./pages/admin/AdminSettings"));
const AdminSeo = React.lazy(() => import("./pages/admin/AdminSeo"));
const AdminWebhooks = React.lazy(() => import("./pages/admin/AdminWebhooks"));
const AdminCoupons = React.lazy(() => import("./pages/admin/AdminCoupons"));
const AdminFunnel = React.lazy(() => import("./pages/admin/AdminFunnel"));
const FunnelSessionDetail = React.lazy(() => import("./pages/admin/funnel/SessionDetail"));
const FunnelVisitorDetail = React.lazy(() => import("./pages/admin/funnel/VisitorDetail"));
const FunnelProductDetail = React.lazy(() => import("./pages/admin/funnel/ProductFunnelDetail"));
const AdminTests = React.lazy(() => import("./pages/admin/AdminTests"));
const TestRunDetail = React.lazy(() => import("./pages/admin/TestRunDetail"));
// AdminRoute is imported directly (not lazy) since it's tiny and needed by every admin page

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,      // 5 min – data stays fresh, no refetch
      gcTime: 30 * 60 * 1000,         // 30 min – keep in memory after unmount
      refetchOnWindowFocus: false,     // don't refetch on tab switch
      retry: 1,                        // only 1 retry on failure
    },
  },
});

// Component to handle cart sync and global prefetching at app level
function CartSyncProvider({ children }: { children: React.ReactNode }) {
  useCartSync();
  // Prefetch card templates & slug translations on app mount
  // so Shop and Product pages render instantly from cache
  React.useEffect(() => {
    import('@/integrations/supabase/client').then(({ supabase }) => {
      queryClient.prefetchQuery({
        queryKey: ['card-templates'],
        queryFn: async () => {
          const { data: templates } = await supabase
            .from('card_templates')
            .select('*')
            .eq('is_active', true)
            .order('display_order', { ascending: true });
          if (!templates) return [];
          const ids = templates.map(t => t.id);
          const { data: fields } = await supabase
            .from('card_template_fields')
            .select('*')
            .in('template_id', ids)
            .order('display_order', { ascending: true });
          return templates.map(t => ({
            ...t,
            photo_position: t.photo_position,
            signature_position: t.signature_position,
            gallery_images: (t.gallery_images as string[]) || [],
            fields: fields?.filter(f => f.template_id === t.id) || [],
          }));
        },
        staleTime: 5 * 60 * 1000,
      });
      queryClient.prefetchQuery({
        queryKey: ['product-slug-translations'],
        queryFn: async () => {
          const { data } = await supabase
            .from('product_slug_translations' as any)
            .select('template_slug, language, localized_silo, localized_slug');
          return data || [];
        },
        staleTime: 60 * 60 * 1000,
      });
      queryClient.prefetchQuery({
        queryKey: ['url-translations'],
        queryFn: async () => {
          const { data } = await supabase
            .from('url_translations')
            .select('*')
            .order('page_key');
          return data || [];
        },
        staleTime: 60 * 60 * 1000,
      });
    });
  }, []);
  return <>{children}</>;
}

// Admin-aware loading fallback – shows the admin layout shell immediately
const AdminLazyFallback = () => (
  <div className="flex items-center justify-center flex-1 min-h-[60vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

// Minimal loading fallback for lazy-loaded routes
const LazyFallback = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <CartSyncProvider>
                <ScrollToTop />
                <Routes>
                  {/* English (default) routes - no prefix */}
                  <Route element={<LanguageRouter />}>
                    {getLocalizedRoutes()}
                  </Route>
                  
                  {/* German routes with /de prefix */}
                  <Route path="de" element={<LanguageRouter />}>
                    {getLocalizedRoutes('de')}
                  </Route>
                  
                  {/* Spanish routes with /es prefix */}
                  <Route path="es" element={<LanguageRouter />}>
                    {getLocalizedRoutes('es')}
                  </Route>
                  
                  {/* French routes with /fr prefix */}
                  <Route path="fr" element={<LanguageRouter />}>
                    {getLocalizedRoutes('fr')}
                  </Route>
                  
                  {/* Portuguese routes with /pt prefix */}
                  <Route path="pt" element={<LanguageRouter />}>
                    {getLocalizedRoutes('pt')}
                  </Route>
                  
                  {/* Russian routes with /ru prefix */}
                  <Route path="ru" element={<LanguageRouter />}>
                    {getLocalizedRoutes('ru')}
                  </Route>
                  
                  {/* Italian routes with /it prefix */}
                  <Route path="it" element={<LanguageRouter />}>
                    {getLocalizedRoutes('it')}
                  </Route>
                  
                  {/* Swedish routes with /sv prefix */}
                  <Route path="sv" element={<LanguageRouter />}>
                    {getLocalizedRoutes('sv')}
                  </Route>
                  
                  {/* Chinese routes with /zh prefix */}
                  <Route path="zh" element={<LanguageRouter />}>
                    {getLocalizedRoutes('zh')}
                  </Route>
                  
                  {/* Auth verification route */}
                  <Route path="/auth/verify" element={<AuthVerify />} />
                  
                  {/* Admin Routes - Lazy loaded, no language prefix */}
                  <Route path="/admin/login" element={<Suspense fallback={<LazyFallback />}><AdminLogin /></Suspense>} />
                  <Route path="/admin" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><AdminDashboard /></Suspense></AdminRoute>} />
                  <Route path="/admin/templates" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><CardTemplates /></Suspense></AdminRoute>} />
                  <Route path="/admin/templates/:id" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><CardTemplateEditor /></Suspense></AdminRoute>} />
                  <Route path="/admin/orders" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><AdminOrders /></Suspense></AdminRoute>} />
                  <Route path="/admin/orders/:orderId" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><OrderDetail /></Suspense></AdminRoute>} />
                  <Route path="/admin/customers" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><AdminCustomers /></Suspense></AdminRoute>} />
                  <Route path="/admin/customers/:customerId" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><CustomerDetail /></Suspense></AdminRoute>} />
                  <Route path="/admin/invoices" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><AdminInvoices /></Suspense></AdminRoute>} />
                  <Route path="/admin/invoices/:invoiceId" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><InvoiceDetail /></Suspense></AdminRoute>} />
                  <Route path="/admin/products" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><AdminProducts /></Suspense></AdminRoute>} />
                  <Route path="/admin/products/:productId" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><ProductDetail /></Suspense></AdminRoute>} />
                  <Route path="/admin/settings" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><AdminSettings /></Suspense></AdminRoute>} />
                  <Route path="/admin/seo" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><AdminSeo /></Suspense></AdminRoute>} />
                  <Route path="/admin/webhooks" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><AdminWebhooks /></Suspense></AdminRoute>} />
                  <Route path="/admin/coupons" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><AdminCoupons /></Suspense></AdminRoute>} />
                  <Route path="/admin/funnel" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><AdminFunnel /></Suspense></AdminRoute>} />
                  <Route path="/admin/funnel/sessions/:sessionId" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><FunnelSessionDetail /></Suspense></AdminRoute>} />
                  <Route path="/admin/funnel/journeys/:visitorId" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><FunnelVisitorDetail /></Suspense></AdminRoute>} />
                  <Route path="/admin/funnel/products/:productId" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><FunnelProductDetail /></Suspense></AdminRoute>} />
                  <Route path="/admin/funnel/:tab" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><AdminFunnel /></Suspense></AdminRoute>} />
                  <Route path="/admin/tests" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><AdminTests /></Suspense></AdminRoute>} />
                  <Route path="/admin/tests/:runId" element={<AdminRoute><Suspense fallback={<AdminLazyFallback />}><TestRunDetail /></Suspense></AdminRoute>} />
                  
                  {/* Catch-all */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                <CookieConsent />
                <ExitIntentPopup />
              </CartSyncProvider>
            </BrowserRouter>
          </TooltipProvider>
        </LanguageProvider>
      </AuthProvider>
    </QueryClientProvider>
);

export default App;
